/** @format */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isStringValid, validateAndTrimString } from 'core-lib/utils/utils';
import { ChipsNFilter } from 'ui-v2/components/ChipsNFilter';
import { Typography } from 'ui-v2/components/Typography';
import { Input } from 'ui-v2/components/Input';
import { Button } from 'ui-v2/components/Button';

import BackButton from 'ui-v2/components/BackButton';
import Modal from 'ui-v2/components/Modal';
import { isNumeric } from 'core-lib/utils/regex';

import { phoneNumberValidation } from 'core-lib/utils/validation';
import { useForm } from 'core-lib/hooks';
import s from './AddAddress.module.scss';
import { AddressTypeList, saveAddressType } from '../../constants/constants';
import { updateUserAddress } from '../../services/updateUserAddress';
import { customToast } from 'core-lib/utils/customToast';
import { AddressType, saveNewAddress } from '../../services/saveNewAddress';
import { calculateTagStatusAndUserAddressType, removeDuplicateValues } from '../../utils/utils';
import { SessionStorage } from 'core-lib/constants/storage';
import { DictType } from 'core-lib/types/Helper';
import { fetchAddressFromLatLng } from 'core-lib/services/fetchAddressFromLatLng';
import { getDefaultAttributes, trackCustomGTMEvent, trackEvent } from 'core-lib/utils/events';
import { EventCategoryName } from 'core-lib/types/Events';
import { getDeviceId } from 'core-lib/utils/source';
import Icon from 'ui-v2/components/Icon/Icon';

type AddAddressProps = {
  isShow: boolean;
  fetchedAddress: any;
  onClose: () => void;
  onDone: () => void;
  disablePhoneField?: boolean;
  isEditAddress: boolean;
  customRootStyle?: DictType;
};

export const AddAddress = ({
  isShow,
  fetchedAddress,
  onClose,
  onDone,
  disablePhoneField = false,
  isEditAddress,
  customRootStyle,
}: AddAddressProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userAddressType, setUserAddressType] = useState<number>(0);
  const { full_name: fullName, phone_number: phoneNumber } = useSelector(
    state => state.auth.profile,
  );

  const [addressTagStatus, setAddressTagStatus] = useState({
    Home: '',
    Office: '',
    Other: '',
  });

  // selectors
  const profile = useSelector(state => state.auth?.profile);

  const { isAuthenticated } = useSelector(state => state.auth);
  const { addressList: userAddresses } = useSelector(state => state.auth.userAddresses);

  const validate = ({ form, name = 'all' }) => {
    const f = { ...form };

    if (name === 'all' || name === 'fullName') {
      f.fullName.err = validateAndTrimString(f.fullName.input).errMsg;
    }
    if (name === 'all' || name === 'phone') {
      f.phone.err = phoneNumberValidation(f.phone.input).errMsg;
    }
    if (name === 'all' || name === 'address') {
      f.address.err = validateAndTrimString(f.address.input).errMsg;
    }

    if (userAddressType === 3) {
      if (name === 'all' || name === 'secAdd') {
        f.secAdd.err = validateAndTrimString(f.secAdd.input).errMsg;
        if (
          f.secAdd.input?.toUpperCase() === 'HOME' ||
          f.secAdd.input?.toUpperCase() === 'OFFICE'
        ) {
          f.secAdd.err = 'This address tag already used for another address';
        }
      }
    } else {
      f.secAdd.err = '';
    }

    return f;
  };

  const submitForm = () => {
    if (Object.keys(form).every(key => form[key].err === '')) {
      handleOnDone();
    }
  };

  const initState = {
    secAdd: {
      input: '',
      err: '',
    },

    address: {
      input: '',
      err: '',
    },
    landmark: {
      input: '',
      err: '',
    },
    fullName: {
      input: profile?.full_name || '',
      err: '',
    },
    phone: {
      input: profile?.phone_number || '',
      err: '',
    },
  };

  const {
    form,
    handleChange: onChange,
    handleSubmit,
  } = useForm({
    callback: submitForm,
    validate,
    initState,
  });

  // add new address
  const addNewAddress = async payload => {
    try {
      if (!isAuthenticated) {
        localStorage.setItem('userAddress', JSON.stringify(payload));
        onDone();
      } else if (isEditAddress) {
        const newPayload = { ...payload, id: fetchedAddress?.id };
        const { response } = await updateUserAddress(newPayload);
        if (response) {
          onDone();
          customToast({
            variant: 'positivePriority',
            label: 'Address updated successfully',
          });
          trackEvent({
            name: 'cta_clicked',
            defaultAttr: getDefaultAttributes(),
            attributes: {
              category: EventCategoryName.Address,
              option_selected: 'Update Address',
            },
          });
        }
      } else {
        const { response }: any = await saveNewAddress(payload);
        if (response) {
          sessionStorage.setItem(SessionStorage.IsNewAddress, JSON.stringify(true));
          onDone();
          trackEvent({
            name: 'cta_clicked',
            defaultAttr: getDefaultAttributes(),
            attributes: {
              category: EventCategoryName.Address,
              option_selected: 'Saved Address',
            },
          });
        }
      }
    } catch (err: any) {
      customToast({
        variant: 'criticalNotPriority',
        label: err?.message || 'Something went wrong',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDone = async () => {
    setIsLoading(true);
    const pincode = fetchedAddress?.pincode ? fetchedAddress.pincode.toString().trim() : '';

    let payload: AddressType = {
      city: fetchedAddress?.city?.trim() || '',
      latitude: fetchedAddress?.latitude || '',
      longitude: fetchedAddress?.longitude || '',
      locality: removeDuplicateValues(fetchedAddress) || '',
      address: form?.address?.input?.trim() || '',
      pincode,
      state: fetchedAddress?.state?.trim() || '',
      landmark: form?.landmark?.input?.trim() || '',
      address_type: userAddressType,
      secondary_address_type: {
        1: AddressTypeList.Home,
        2: AddressTypeList.Office,
        3: form?.secAdd?.input?.trim(),
      }[userAddressType],
      email_id: profile?.email_id || '',
      user_address_meta: {
        name: form?.fullName?.input?.trim() || '',
        phone_number: form?.phone?.input?.trim() || '',
      },
      state_code: '',
    };

    try {
      // Retrieve standardized state values from the infra service.
      // This step ensures that state values conform to a consistent format
      // and can be easily used throughout the application.
      const { locationData, error } = await fetchAddressFromLatLng({
        pincode,
        requiredFields: ['state'],
      });

      if (locationData?.state) {
        payload = {
          ...payload,
          state: locationData.state,
          state_code: locationData?.stateCode,
        };
        addNewAddress(payload);
      } else {
        customToast({
          variant: 'criticalNotPriority',
          label: error || error?.message || 'Something went wrong',
        });
        setIsLoading(false);
      }
    } catch (err: any) {
      customToast({
        variant: 'criticalNotPriority',
        label: err?.message || 'Something went wrong',
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // checkAddressStatus();
    const { tagStatusMap, userAddressType } = calculateTagStatusAndUserAddressType(
      isEditAddress,
      fetchedAddress,
      userAddresses,
    );
    setUserAddressType(userAddressType);
    setAddressTagStatus(tagStatusMap);
  }, [fetchedAddress, isEditAddress, userAddresses]);

  useEffect(() => {
    if (fetchedAddress) {
      if (isStringValid(fetchedAddress?.metaData?.name)) {
        onChange('fullName', fetchedAddress?.metaData?.name);
      }
      onChange('phone', fetchedAddress?.metaData?.phone || profile?.phone_number);
      onChange('landmark', fetchedAddress?.landmark);
      onChange('secAdd', fetchedAddress?.addressTag);
      onChange('address', fetchedAddress?.address);
    }
  }, [fetchedAddress]);

  useEffect(() => {
    if (isStringValid(fullName)) {
      onChange('fullName', fullName);
    }
    if (phoneNumber) {
      onChange('phone', phoneNumber);
    }
  }, [phoneNumber, fullName]);

  return (
    <Modal
      show={isShow}
      head={{
        show: true,
        title: 'Enter complete address',
        onClose: onClose,
      }}
      closeOnClickOutside={true}
      noPadding
      animation={{ type: 'btt', timeout: 's' }}
      rootStyle={{ ...customRootStyle }}
      customeBodyStyle={{ maxHeight: '85vh' }}
    >
      <div className={s.address_popup_root} id="inputWrap">
        <BackButton type="hidden" onBack={onClose} />

        <div className={s.currentLocation}>
          {/* <Typography
            label="Selected location"
            variant="label_3_medium"
          /> */}
          <div className={s.loader}>
            <div className={s.icon}>
              <Icon name="LocationRegular" />
            </div>
            <div className={s.text}>
              <div className={s.textWrap} onClick={onClose}>
                <Typography
                  label={fetchedAddress?.name || removeDuplicateValues(fetchedAddress)}
                  variant="label_2_medium"
                />
                <Icon name="Edit2Regular" width={20} height={20} />
              </div>

              <Typography
                label={
                  fetchedAddress?.vicinity ||
                  fetchedAddress?.addressLine2 ||
                  `${fetchedAddress?.state || ''} ${fetchedAddress?.city || ''} ${fetchedAddress?.pincode || ''}`
                }
                variant="body_4_regular"
                color="secondary"
              />
            </div>
          </div>
          <div className={s.updated_text}>
            <Typography
              label={`Updated based on your exact map pin`}
              variant="tiny_medium"
              color="tertiary"
            />
          </div>
        </div>

        <div className={s.input_container}>
          <div className={s.input_parent}>
            <Input
              inputType="text"
              name="address"
              label="House no. / floor / building"
              required={true}
              showRequiredAsterisk
              placeholder="Enter house no."
              value={form.address.input}
              // @ts-ignore
              onChange={e => onChange('address', e?.target?.value)}
              hasError={form.address.err !== ''}
              errorText={form.address.err}
            />
          </div>

          <div className={s.input_parent}>
            <Input
              inputType="text"
              name="landmark"
              label="Landmark (Optional)"
              placeholder="eg: near apollo hospital"
              value={form.landmark.input}
              // @ts-ignore
              onChange={e => onChange('landmark', e.target.value)}
              hasError={form.landmark.err !== ''}
              errorText={form.landmark.err}
            />
          </div>

          <div className={s.input_parent}>
            <Input
              label="Name "
              required={true}
              showRequiredAsterisk
              inputType="text"
              placeholder="Enter your name"
              value={form.fullName.input}
              hasError={form.fullName.err !== ''}
              errorText={form.fullName.err}
              onChange={(e: any) => {
                onChange('fullName', e.target.value);
              }}
            />
          </div>

          <div className={s.input_parent}>
            <Input
              label="Contact number "
              required={true}
              showRequiredAsterisk
              inputType="numeric"
              placeholder="Enter contact number"
              value={form.phone.input}
              hasError={form.phone.err !== ''}
              errorText={form.phone.err}
              onChange={e => {
                // @ts-ignore
                if (isNumeric(e.currentTarget.value)) {
                  onChange('phone', e.currentTarget.value);
                }
              }}
              maxLength={10}
            />
          </div>

          <div className={s.section}>
            <Typography label="Save address as " variant="label_3_medium" />
            <div className={s.chips}>
              {saveAddressType.map((item, index) => (
                <div
                  onClick={() => {
                    if (addressTagStatus[item.name] !== 'disabled') {
                      setUserAddressType(item.id);
                      setAddressTagStatus((prev: any) => ({
                        ...{
                          Home:
                            (prev.Home === 'selected' && 'enabled') ||
                            (prev.Home === 'disabled' && 'disabled'),
                          Office:
                            (prev.Office === 'selected' && 'enabled') ||
                            (prev.Office === 'disabled' && 'disabled'),
                          Other:
                            (prev.Other === 'selected' && 'enabled') ||
                            (prev.Other === 'disabled' && 'disabled'),
                        },
                        [item.name]: 'selected',
                      }));

                      if (item.name === 'Other') {
                        const element = global.document.getElementById('inputWrap');
                        if (element) {
                          setTimeout(() => {
                            element.scrollTo({
                              top: 10000,
                              behavior: 'smooth',
                            });
                          }, 120);
                        }
                      }
                    } else {
                      customToast({
                        variant: 'positivePriority',
                        label: 'This address tag already used for another address',
                      });
                    }
                  }}
                >
                  <ChipsNFilter
                    key={index}
                    label={item.name}
                    size="large"
                    isSelected={addressTagStatus[item.name] === 'selected'}
                    isDisabled={addressTagStatus[item.name] === 'disabled'}
                    // state={addressTagStatus[item.name]}
                  />
                </div>
              ))}
            </div>
            {userAddressType === 3 && (
              <>
                <div className={s.input_parent}>
                  <Input
                    inputType="text"
                    name="secondaryAddress"
                    placeholder="Save as (eg: friend’s house)"
                    value={form.secAdd.input}
                    // @ts-ignore
                    onChange={e => {
                      onChange('secAdd', e.currentTarget.value);
                    }}
                    hasError={form.secAdd.err !== ''}
                    errorText={form.secAdd.err}
                    maxLength={15}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className={s.button_container}>
          <Button
            size="large"
            label={isEditAddress ? 'Update address' : 'Save address'}
            variant="primary"
            onClick={e => {
              trackCustomGTMEvent({
                eventName: 'pm_35_confirm_location',
                attributes: {
                  category: EventCategoryName.Store,
                  device_id: getDeviceId(),
                },
              });

              handleSubmit(e);
            }}
            loading={isLoading}
            disabled={isLoading}
            fullWidth
          />
        </div>
      </div>
    </Modal>
  );
};
